import { useEffect, useMemo, useRef, useState } from 'react'
import styled from '@emotion/styled'
import { useDropzone } from 'react-dropzone'
import { LinearProgress, CircularProgress } from '@mui/material'
import DriveFolderUploadOutlinedIcon from '@mui/icons-material/DriveFolderUploadOutlined'
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined'
import FolderOutlinedIcon from '@mui/icons-material/FolderOutlined'
import CreateNewFolderOutlinedIcon from '@mui/icons-material/CreateNewFolderOutlined'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'
import ScheduleOutlinedIcon from '@mui/icons-material/ScheduleOutlined'
import HighlightOffOutlinedIcon from '@mui/icons-material/HighlightOffOutlined'
import CloseIcon from '@mui/icons-material/Close'
import { uploadOne, runQueue, UPLOAD_CONCURRENCY } from '../../api/uploads.js'
import { loadLimits } from '../../api/limits.js'
import { capFor, capNote, formatCap } from './uploadCaps.js'
import { pickDirectoryFiles } from '../files/directory.js'
import { tokens } from '../../theme.js'
import {
  Dialog,
  DialogTitle,
  DialogActions,
  Button,
  SecondaryButton,
  MedBodyText,
  MutedText,
  NativeSelect,
  InlineMessage,
  IconOnlyButton,
  plural,
  barSx
} from '../styles/index.js'
import { formatSize } from '../common/text.js'

const colors = tokens.colors
const fontFamily = tokens.fonts.body

const Zone = styled.div`
  margin-top: 18px;
  padding: 28px 20px 24px;
  border-radius: 12px;
  text-align: center;
  border: 1.5px solid ${colors.primary};
  background: ${p => (p.$over ? colors.primaryLight : colors.background.light)};
  outline: ${p => (p.$over ? `3px solid ${colors.primaryExtraLight}` : 'none')};
  transition: background 0.15s ease;
`
const List = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: 16px;
  max-height: 320px;
  overflow-y: auto;
`
const Row = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  min-height: 34px;
  padding: 7px 8px 6px;
  border-bottom: 1px solid ${colors.background.medium};
  border-radius: ${p => (p.$failed ? '8px' : '0')};
  background: ${p => (p.$failed ? colors.errorLight : 'transparent')};
  font: 400 14px/1.3 ${fontFamily};
  color: ${colors.text.body};
  & .path {
    flex: 1;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  & .size {
    font-size: 12px;
    line-height: 18px;
    color: ${colors.text.muted};
    white-space: nowrap;
  }
  & .reason {
    font: 400 12px/1.4 ${fontFamily};
    color: ${p => (p.$failed && !p.$settled ? colors.error : colors.text.muted)};
  }
  & > .MuiSvgIcon-root,
  & > .MuiCircularProgress-root {
    margin-top: 1px;
  }
`
const RowIcon = { done: CheckCircleOutlineIcon, failed: ErrorOutlineIcon, queued: ScheduleOutlinedIcon, cancelled: HighlightOffOutlinedIcon }
const ROW_TONE = { done: colors.status.ready, failed: colors.error, queued: colors.text.muted, cancelled: colors.text.muted }

const byDepthThenName = (a, b) => a.split('/').length - b.split('/').length || a.localeCompare(b)
const joinNames = list => (list.length > 1 ? `${list.slice(0, -1).join(', ')} and ${list.at(-1)}` : list[0])
const REFUSED = {
  NAME_TOO_LONG: 'Name over 255 characters',
  NAME_INVALID: 'Name or folder path with a slash, a control character or ".."',
  PATH_TOO_DEEP: 'Folder path over 32 levels deep',
  CANCELLED: 'Cancelled'
}
const reasonFor = (code, cap, settled) =>
  code === 'TOO_LARGE'
    ? `Exceeds the ${formatCap(cap)} per-file cap${settled ? '. Split or compress it, then retry.' : ''}`
    : REFUSED[code]
      ? `${REFUSED[code]}${settled && code !== 'CANCELLED' ? '. Rename it, then retry.' : ''}`
      : `Could not be stored${settled ? '. Try again.' : ''}`

function relativePathOf(file) {
  const p = (file.webkitRelativePath || file.path || '').replace(/^\.?\//, '')
  return p && p !== file.name ? p : ''
}
let seq = 0
const toItem = file => ({ id: `u${++seq}`, file, path: relativePathOf(file), size: file.size, state: 'queued', progress: 0 })

function folderOptions(folders) {
  const kids = new Map()
  for (const g of folders) {
    const k = g.parentId || null
    if (!kids.has(k)) kids.set(k, [])
    kids.get(k).push(g)
  }
  const out = []
  const walk = (parent, depth) => {
    for (const g of kids.get(parent) || []) {
      out.push({ id: g.id, label: `${'   '.repeat(depth)}${g.name}` })
      walk(g.id, depth + 1)
    }
  }
  walk(null, 0)
  return out
}

export function UploadDialog({ open, projectId, projectName, folders, folderId: initialFolderId, seed, onChanged, onClose }) {
  const [folderId, setFolderId] = useState(initialFolderId || '')
  const [items, setItems] = useState([])
  const [phase, setPhase] = useState('pick') // pick | uploading | finished
  const [limits, setLimits] = useState(null)
  const [createdFolders, setCreatedFolders] = useState([])
  const controllers = useRef(new Map())
  const cancelledRef = useRef(false)
  const folderInput = useRef(null)

  useEffect(() => {
    if (!open) return
    setFolderId(initialFolderId || '')
    setItems(seed ? seed.map(toItem) : [])
    setPhase('pick')
    setCreatedFolders([])
    cancelledRef.current = false
    loadLimits()
      .then(setLimits)
      .catch(() => {})
  }, [open])

  const addFiles = files => {
    const added = Array.from(files).map(toItem)
    setItems(list => [...list, ...added])
  }
  const {
    getRootProps,
    getInputProps,
    isDragActive,
    open: pickFiles
  } = useDropzone({
    onDrop: addFiles,
    noClick: true,
    noKeyboard: true,
    useFsAccessApi: false,
    disabled: phase !== 'pick'
  })

  const patch = (id, p) => setItems(list => list.map(it => (it.id === id ? { ...it, ...(typeof p === 'function' ? p(it) : p) } : it)))

  async function start(list = items) {
    if (!list.length) return
    setPhase('uploading')
    let lastRefresh = 0
    const refresh = (what, force) => {
      if (force || Date.now() - lastRefresh > 800) {
        lastRefresh = Date.now()
        onChanged(what)
      }
    }
    await runQueue(list, UPLOAD_CONCURRENCY, async it => {
      if (cancelledRef.current) {
        patch(it.id, { state: 'cancelled' })
        return
      }
      const ctrl = new AbortController()
      controllers.current.set(it.id, ctrl)
      patch(it.id, { state: 'uploading', progress: 0 })
      try {
        const res = await uploadOne({
          projectId,
          folderId: folderId || null,
          file: it.file,
          relativePath: it.path,
          signal: ctrl.signal,
          onProgress: p => patch(it.id, { progress: p })
        })
        if (res.createdFolders.length) setCreatedFolders(prev => [...new Set([...prev, ...res.createdFolders])].sort(byDepthThenName))
        if (res.failed.length) patch(it.id, { state: 'failed', code: res.failed[0].code })
        else patch(it.id, { state: 'done', progress: 1, uploaded: res.uploaded[0] })
        refresh({ files: true, folders: res.createdFolders.length > 0 })
      } catch (err) {
        patch(it.id, { state: ctrl.signal.aborted ? 'cancelled' : 'failed', code: ctrl.signal.aborted ? 'CANCELLED' : 'STORE_FAILED' })
      } finally {
        controllers.current.delete(it.id)
      }
    })
    refresh({ files: true, folders: true }, true)
    setPhase('finished')
  }
  useEffect(() => {
    if (open && seed?.length && phase === 'pick' && items.length === seed.length) start(items)
  }, [open, items])

  function cancelRemaining() {
    cancelledRef.current = true
    for (const ctrl of controllers.current.values()) ctrl.abort()
  }
  function cancelOne(id) {
    controllers.current.get(id)?.abort()
  }
  function chooseOther() {
    setItems([])
    setCreatedFolders([])
    cancelledRef.current = false
    setPhase('pick')
  }

  function chooseFolderInput(e) {
    const picked = Array.from(e.target.files || [])
    e.target.value = ''
    if (!picked.length) return
    const added = picked.map(toItem)
    const next = [...items, ...added]
    setItems(next)
    start(next)
  }

  async function pickFolder() {
    if (phase !== 'pick') return
    let picked
    try {
      picked = await pickDirectoryFiles()
    } catch {
      picked = null
    }
    if (picked !== null) {
      if (!picked.length) return
      const next = [...items, ...picked.map(toItem)]
      setItems(next)
      start(next)
      return
    }
    folderInput.current?.click()
  }

  const counts = useMemo(() => {
    const n = { done: 0, uploading: 0, failed: 0, queued: 0, cancelled: 0, bytes: 0 }
    for (const it of items) {
      n[it.state] += 1
      if (it.state === 'done') n.bytes += it.uploaded?.sizeBytes ?? it.size
    }
    return n
  }, [items])
  const overall = items.length
    ? items.reduce((s, it) => s + (it.state === 'done' ? 1 : it.state === 'uploading' ? it.progress : 0), 0) / items.length
    : 0
  const destination = folders.find(g => g.id === folderId)?.name || projectName || 'the project'
  const failedItems = items.filter(it => it.state === 'failed' || it.state === 'cancelled')
  const stoppedLabel = counts.failed && counts.cancelled ? 'were not uploaded' : counts.cancelled ? 'were cancelled' : 'could not be uploaded'
  const wide = phase !== 'pick' || items.length > 0

  return (
    <Dialog open={open} onClose={phase === 'uploading' ? undefined : onClose} width={wide ? 520 : 420} data-testid="upload-dialog">
      {phase === 'pick' && (
        <>
          <DialogTitle style={{ marginBottom: 4 }} data-testid="upload-title">
            Upload files
          </DialogTitle>
          <MutedText>Into {destination}</MutedText>
          <MedBodyText style={{ margin: '18px 0 6px' }}>Destination folder</MedBodyText>
          <NativeSelect value={folderId} onChange={e => setFolderId(e.target.value)} data-testid="upload-folder-select">
            <option value="">All files (top level)</option>
            {folderOptions(folders).map(o => (
              <option key={o.id} value={o.id}>
                {o.label}
              </option>
            ))}
          </NativeSelect>

          <Zone {...getRootProps()} $over={isDragActive} data-testid="upload-dropzone" data-over={isDragActive}>
            <input {...getInputProps()} data-testid="file-input" />
            <input ref={folderInput} type="file" webkitdirectory="" multiple hidden data-testid="folder-input" onChange={chooseFolderInput} />
            <DriveFolderUploadOutlinedIcon sx={{ fontSize: 28, color: colors.primary }} />
            <div style={{ font: `500 15px/1.4 ${fontFamily}`, color: colors.text.heading, marginTop: 8 }}>
              {isDragActive ? 'Drop to add' : 'Drag files or folders here'}
            </div>
            <MutedText style={{ fontSize: 12, marginTop: 4 }}>Dropped folders keep their structure as subfolders.{capNote(limits)}</MutedText>
            <div style={{ display: 'flex', gap: 12, justifyContent: 'center', marginTop: 16 }}>
              <SecondaryButton type="button" padding="short" onClick={pickFiles} data-testid="choose-files">
                <DescriptionOutlinedIcon /> Choose files
              </SecondaryButton>
              <SecondaryButton type="button" padding="short" onClick={pickFolder} data-testid="choose-folder">
                <FolderOutlinedIcon /> Upload folder
              </SecondaryButton>
            </div>
          </Zone>

          {items.length > 0 && (
            <List data-testid="upload-list">
              {items.map(it => (
                <ItemRow key={it.id} item={it} cap={capFor(it.file, limits)} />
              ))}
            </List>
          )}
          <DialogActions>
            <SecondaryButton type="button" onClick={onClose} data-testid="upload-cancel">
              Cancel
            </SecondaryButton>
            <Button type="button" disabled={!items.length} onClick={() => start()} data-testid="upload-confirm">
              Upload
            </Button>
          </DialogActions>
        </>
      )}

      {phase === 'uploading' && (
        <>
          <DialogTitle style={{ marginBottom: 4 }} data-testid="upload-title">
            Uploading {plural(items.length, 'file')}…
          </DialogTitle>
          <MutedText data-testid="upload-summary">
            {counts.done} done · {counts.uploading} in progress · {counts.failed} failed · {counts.queued} queued
            {counts.cancelled ? ` · ${counts.cancelled} cancelled` : ''}. Streaming to storage, you can keep working
          </MutedText>
          <LinearProgress
            variant="determinate"
            value={overall * 100}
            data-testid="upload-overall"
            sx={barSx(colors.primary, { mt: '14px', height: 6, borderRadius: 3, '& .MuiLinearProgress-bar': { borderRadius: 3 } })}
          />
          <List data-testid="upload-list">
            {items.map(it => (
              <ItemRow key={it.id} item={it} cap={capFor(it.file, limits)} onCancel={cancelOne} />
            ))}
          </List>
          {createdFolders.length > 0 && (
            <InlineMessage tone="info" icon={CreateNewFolderOutlinedIcon} style={{ marginTop: 16 }} data-testid="upload-folders-note">
              Created {createdFolders.length === 1 ? 'folder' : 'folders'} {joinNames(createdFolders)} from the dropped folder.
            </InlineMessage>
          )}
          <DialogActions>
            <SecondaryButton type="button" onClick={cancelRemaining} data-testid="upload-cancel">
              Cancel remaining
            </SecondaryButton>
            <Button type="button" disabled data-testid="upload-close">
              Done
            </Button>
          </DialogActions>
        </>
      )}

      {phase === 'finished' && (
        <>
          <DialogTitle data-testid="upload-title">Upload finished</DialogTitle>
          <InlineMessage tone={counts.done ? 'success' : 'error'} data-testid="upload-result">
            {counts.done} of {plural(items.length, 'file')} uploaded to {destination}
            {counts.bytes ? ` (${formatSize(counts.bytes)})` : ''}.
          </InlineMessage>
          {createdFolders.length > 0 && (
            <InlineMessage tone="info" icon={CreateNewFolderOutlinedIcon} style={{ marginTop: 12 }} data-testid="upload-folders-note">
              Created {createdFolders.length === 1 ? 'folder' : 'folders'} {joinNames(createdFolders)} from the dropped folder.
            </InlineMessage>
          )}
          {failedItems.length > 0 && (
            <>
              <MedBodyText style={{ margin: '18px 0 10px' }}>
                {plural(failedItems.length, 'file')} {stoppedLabel}:
              </MedBodyText>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {failedItems.map(it => (
                  <ItemRow key={it.id} item={it} cap={capFor(it.file, limits)} settled />
                ))}
              </div>
            </>
          )}
          <DialogActions>
            <SecondaryButton type="button" onClick={chooseOther} data-testid="upload-more">
              <DescriptionOutlinedIcon /> Choose other files
            </SecondaryButton>
            <Button type="button" onClick={onClose} data-testid="upload-close">
              Close
            </Button>
          </DialogActions>
        </>
      )}
    </Dialog>
  )
}

function ItemRow({ item, cap, onCancel, settled }) {
  const stopped = item.state === 'failed' || item.state === 'cancelled'
  const Icon = RowIcon[item.state]
  const name = item.path || item.file.name
  return (
    <Row $failed={item.state === 'failed'} $settled={settled} data-testid="upload-item" data-name={name} data-state={item.state}>
      {item.state === 'uploading' ? (
        <CircularProgress size={16} thickness={5} disableShrink sx={{ color: colors.status.loading, flex: '0 0 auto' }} />
      ) : (
        <Icon sx={{ fontSize: 18, color: ROW_TONE[item.state], flex: '0 0 auto' }} />
      )}
      <div className="path">
        <div style={{ overflow: 'hidden', textOverflow: 'ellipsis' }} title={name}>
          {name}
          {settled && ` · ${formatSize(item.size)}`}
        </div>
        {item.state === 'uploading' && (
          <LinearProgress
            variant="determinate"
            value={item.progress * 100}
            data-testid="upload-item-progress"
            sx={barSx(colors.status.loading, { mt: '4px' })}
          />
        )}
        {stopped && (
          <div className="reason" data-testid="upload-item-error">
            {reasonFor(item.code, cap, settled)}
          </div>
        )}
      </div>
      {!settled && <span className="size">{formatSize(item.size)}</span>}
      {item.state === 'uploading' && onCancel && (
        <IconOnlyButton
          type="button"
          onClick={() => onCancel(item.id)}
          title="Cancel"
          data-testid="upload-item-cancel"
          style={{ padding: 2, color: colors.text.muted }}>
          <CloseIcon sx={{ fontSize: 16 }} />
        </IconOnlyButton>
      )}
    </Row>
  )
}
